import { useState, useMemo } from 'react';
import {
  CheckCircle2,
  Clock,
  AlertTriangle,
  Copy,
  Check,
  Download,
  Loader2,
  ExternalLink,
} from 'lucide-react';

const PLATFORMS_DNS = [
  { id: 'cloudflare',      label: 'Cloudflare' },
  { id: 'godaddy',         label: 'GoDaddy' },
  { id: 'namecheap',       label: 'Namecheap' },
  { id: 'google-domains',  label: 'Google Domains' },
  { id: 'route53',         label: 'AWS Route 53' },
  { id: 'hover',           label: 'Hover' },
  { id: '123-reg',         label: '123-reg' },
  { id: 'other',           label: 'Other' },
];

const PLATFORMS_CMS = [
  { id: 'wordpress',   label: 'WordPress' },
  { id: 'shopify',     label: 'Shopify' },
  { id: 'squarespace', label: 'Squarespace' },
  { id: 'wix',         label: 'Wix' },
  { id: 'other',       label: 'Other' },
];

const DNS_INSTRUCTIONS = {
  cloudflare: [
    'Log in to dash.cloudflare.com.',
    'Select your domain.',
    'Go to DNS → Records → Add record.',
    'Type: TXT, Name: @, Content: paste the value below, TTL: Auto.',
    'Click Save.',
  ],
  godaddy: [
    'Log in to godaddy.com → My Products.',
    'Click DNS next to your domain.',
    'Click Add New Record.',
    'Type: TXT, Host: @, TXT Value: paste the value below, TTL: 1 hour.',
    'Click Save.',
  ],
  namecheap: [
    'Log in to namecheap.com → Domain List.',
    'Click Manage next to your domain.',
    'Go to Advanced DNS tab.',
    'Click Add New Record → TXT Record.',
    'Host: @, Value: paste the value below, TTL: Automatic.',
    'Click the green tick to save.',
  ],
  'google-domains': [
    'Log in to domains.google.com.',
    'Click your domain → DNS.',
    'Scroll to Custom records → Manage custom records.',
    'Type: TXT, Host name: leave blank, Data: paste the value below, TTL: 3600.',
    'Click Save.',
  ],
  route53: [
    'Log in to AWS Console → Route 53 → Hosted zones.',
    'Click your domain → Create record.',
    'Record type: TXT, Record name: leave blank.',
    'Value: paste the value below in quotes — "acodia-verify-token".',
    'TTL: 300 → Create records.',
  ],
  hover: [
    'Log in to hover.com → Your Domains.',
    'Click your domain → DNS tab.',
    'Add A Record → choose TXT.',
    'Hostname: @, Value: paste the value below, TTL: default.',
    'Click Save.',
  ],
  '123-reg': [
    'Log in to 123-reg.co.uk → Control Panel.',
    'Click Manage next to your domain → DNS.',
    'Add new record → Type: TXT, Hostname: @, TTL: 3600.',
    'Value: paste the value below.',
    'Click Add.',
  ],
  other: [
    'Add a TXT record with Name @ and the value shown below.',
    'Check your DNS provider documentation if unsure.',
  ],
};

const FILE_INSTRUCTIONS = {
  wordpress: {
    steps: [
      'Log in to your hosting control panel — cPanel, Plesk, or similar.',
      'Open File Manager → public_html folder.',
      'Upload the downloaded file here.',
      'The file must be accessible at yourdomain.com/acodia-verify-[token].html.',
      'Click Verify Now below.',
    ],
  },
  shopify: {
    note: 'Shopify does not allow custom file uploads to the root. Please use the DNS TXT Record method or Meta Tag method instead.',
    switchTo: 'dns',
    switchLabel: 'Switch to DNS method',
  },
  squarespace: {
    note: 'Squarespace does not support root file uploads. Please use the Meta Tag method instead.',
    switchTo: 'meta',
    switchLabel: 'Switch to Meta Tag method',
  },
  wix: {
    note: 'Wix does not support root file uploads. Please use the DNS TXT Record method instead.',
    switchTo: 'dns',
    switchLabel: 'Switch to DNS method',
  },
  other: {
    steps: [
      'Upload the file to the root directory of your website so it is accessible at the URL shown above.',
    ],
  },
};

const META_INSTRUCTIONS = {
  wordpress: {
    optionAHeading: 'Option A — Plugin method (recommended)',
    optionASteps: [
      'Install the free plugin "Insert Headers and Footers" from Plugins → Add New.',
      'Go to Settings → Insert Headers and Footers.',
      'Paste the code into the Scripts in Header box.',
      'Click Save.',
    ],
    optionBHeading: 'Option B — Theme method',
    optionBSteps: [
      'Go to Appearance → Theme Editor → header.php.',
      'Find the closing </head> tag.',
      'Paste the code immediately before it.',
      'Click Update File.',
    ],
  },
  shopify: {
    steps: [
      'Go to Online Store → Themes → Actions → Edit Code.',
      'Click theme.liquid in the layout folder.',
      'Find </head> and paste the code immediately before it.',
      'Click Save.',
    ],
  },
  squarespace: {
    steps: [
      'Go to Settings → Advanced → Code Injection.',
      'Paste the code into the Header field.',
      'Click Save.',
    ],
  },
  wix: {
    steps: [
      'Go to Settings → Custom Code.',
      'Click Add Custom Code.',
      'Paste the code, select All Pages, place in Head.',
      'Click Apply.',
    ],
  },
  other: {
    steps: ['Add the meta tag inside the head section of your homepage HTML, before the closing head tag.'],
  },
};

const METHOD_TABS = [
  { id: 'dns',  label: 'DNS TXT Record' },
  { id: 'file', label: 'HTML File' },
  { id: 'meta', label: 'Meta Tag' },
];

function CopyButton({ value, testId }) {
  const [copied, setCopied] = useState(false);
  const doCopy = async () => {
    try {
      await navigator.clipboard.writeText(value);
    } catch {
      const ta = document.createElement('textarea');
      ta.value = value;
      document.body.appendChild(ta);
      ta.select();
      document.execCommand('copy');
      document.body.removeChild(ta);
    }
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };
  return (
    <button
      type="button"
      onClick={doCopy}
      data-testid={testId}
      className="inline-flex items-center gap-1.5 font-body text-xs px-2.5 py-1 rounded-[3px]"
      style={{
        background: copied ? 'rgba(74,222,128,0.1)' : 'rgba(139,92,246,0.08)',
        border: `1px solid ${copied ? 'rgba(74,222,128,0.25)' : 'var(--b2)'}`,
        color: copied ? 'var(--ok)' : 'var(--a-bright)',
      }}
    >
      {copied ? <Check size={12} /> : <Copy size={12} />}
      {copied ? 'Copied' : 'Copy'}
    </button>
  );
}

function triggerDownloadHtml(token) {
  const html = `${token}`;
  const blob = new Blob([html], { type: 'text/html' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `acodia-verify-${token}.html`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

export default function DomainVerificationCard({ domain, onVerified, authFetch, backendUrl }) {
  const [method, setMethod] = useState(domain.verification_method
    ? ({ dns_txt: 'dns', html_file: 'file', meta_tag: 'meta' }[domain.verification_method])
    : 'dns');
  const [platform, setPlatform] = useState('cloudflare');
  const [cmsPlatform, setCmsPlatform] = useState('wordpress');

  const [verifying, setVerifying] = useState(false);
  const [localStatus, setLocalStatus] = useState({
    verified: domain.verified,
    message: null,
    error: null,
    verifiedAt: domain.verified_at,
    monitoringActive: domain.monitoring_active,
    retryUntil: domain.retry_until,
  });

  const verifyToken = domain.verification_token;
  const dnsValue = `acodia-verification=${verifyToken}`;
  const metaTag = `<meta name="acodia-verification" content="${verifyToken}">`;
  const fileUrl = `${domain.domain}/acodia-verify-${verifyToken}.html`;

  const status = useMemo(() => {
    if (localStatus.verified) return 'verified';
    if (localStatus.error) return 'failed';
    return 'pending';
  }, [localStatus]);

  const runVerify = async () => {
    setVerifying(true);
    setLocalStatus((s) => ({ ...s, message: null, error: null }));
    try {
      const res = await authFetch(
        `${backendUrl}/api/domains/verify/${encodeURIComponent(domain.id)}?method=${method}`,
        { method: 'POST' },
      );
      const data = await res.json().catch(() => ({}));
      if (res.ok && data.verified) {
        setLocalStatus({
          verified: true,
          message: data.message,
          error: null,
          verifiedAt: data.verified_at,
          monitoringActive: true,
          retryUntil: null,
        });
        if (typeof onVerified === 'function') onVerified(domain.id);
      } else {
        setLocalStatus((s) => ({
          ...s,
          verified: false,
          message: null,
          error: data?.message || data?.detail?.error || 'Verification failed. Please try again.',
          retryUntil: data?.retry_until || s.retryUntil,
        }));
      }
    } catch (err) {
      setLocalStatus((s) => ({ ...s, error: err?.message || 'Network error. Please try again.' }));
    } finally {
      setVerifying(false);
    }
  };

  const StatusBadge = () => {
    if (status === 'verified') {
      return (
        <span
          data-testid="domain-status-verified"
          className="inline-flex items-center gap-2 px-2.5 py-1 rounded-[3px] font-body text-xs"
          style={{ background: 'rgba(74,222,128,0.1)', border: '1px solid rgba(74,222,128,0.25)', color: 'var(--ok)' }}
        >
          <span
            className="dom-pulse"
            style={{ width: 8, height: 8, borderRadius: 999, background: 'var(--ok)', display: 'inline-block' }}
          />
          Verified
        </span>
      );
    }
    if (status === 'failed') {
      return (
        <span
          data-testid="domain-status-failed"
          className="inline-flex items-center gap-2 px-2.5 py-1 rounded-[3px] font-body text-xs"
          style={{ background: 'rgba(248,113,113,0.1)', border: '1px solid rgba(248,113,113,0.25)', color: 'var(--danger)' }}
        >
          <AlertTriangle size={12} /> Failed
        </span>
      );
    }
    return (
      <span
        data-testid="domain-status-pending"
        className="inline-flex items-center gap-2 px-2.5 py-1 rounded-[3px] font-body text-xs"
        style={{ background: 'rgba(251,176,64,0.1)', border: '1px solid rgba(251,176,64,0.25)', color: 'var(--warn)' }}
      >
        <Clock size={12} /> Pending Verification
      </span>
    );
  };

  return (
    <div
      data-testid={`domain-verify-card-${domain.domain}`}
      className="p-6 rounded-lg h-full flex flex-col"
      style={{ background: 'var(--l1)', border: '1px solid var(--b1)' }}
    >
      <div className="flex items-center justify-between gap-3 mb-4">
        <span
          className="font-mono text-sm break-all"
          style={{ color: 'var(--a)' }}
          data-testid="domain-name"
        >
          {domain.domain}
        </span>
        <StatusBadge />
      </div>

      {status === 'verified' ? (
        <div
          className="flex-1 p-4 rounded"
          style={{ background: 'rgba(74,222,128,0.06)', border: '1px solid rgba(74,222,128,0.2)' }}
          data-testid="domain-monitoring-active"
        >
          <div className="flex items-center gap-2 mb-2" style={{ color: 'var(--ok)' }}>
            <CheckCircle2 size={16} />
            <span className="font-body text-sm font-medium">Monitoring Active</span>
          </div>
          <p className="font-body text-xs" style={{ color: 'rgba(240,237,248,0.75)', lineHeight: 1.6 }}>
            Acodia Watch is monitoring this domain continuously.
            {localStatus.verifiedAt ? ` Verified on ${new Date(localStatus.verifiedAt).toLocaleString('en-GB')}.` : ''}
          </p>
        </div>
      ) : (
        <>
          {/* Method tabs */}
          <div className="flex gap-1.5 mb-4 flex-wrap">
            {METHOD_TABS.map((m) => {
              const active = method === m.id;
              return (
                <button
                  key={m.id}
                  type="button"
                  data-testid={`domain-tab-${m.id}`}
                  onClick={() => setMethod(m.id)}
                  className="font-body text-xs px-3 py-1.5 rounded-[3px] transition-colors"
                  style={{
                    background: active ? 'rgba(139,92,246,0.12)' : 'transparent',
                    border: `1px solid ${active ? 'var(--b3)' : 'var(--b1)'}`,
                    color: active ? 'var(--white)' : 'var(--ts)',
                  }}
                >
                  {m.label}
                </button>
              );
            })}
          </div>

          {/* DNS tab */}
          {method === 'dns' && (
            <div className="flex-1">
              <p className="font-body text-sm mb-3" style={{ color: 'rgba(240,237,248,0.78)' }}>
                Add a TXT record to your domain DNS settings.
              </p>
              <label className="font-body text-xs mb-1.5 block" style={{ color: 'var(--ts)' }}>
                Which registrar or DNS provider are you using?
              </label>
              <select
                data-testid="domain-dns-platform"
                value={platform}
                onChange={(e) => setPlatform(e.target.value)}
                className="w-full font-body text-sm h-10 px-3 rounded-[3px] mb-4"
                style={{ background: 'rgba(255,255,255,0.025)', border: '1px solid var(--b1)', color: 'var(--white)' }}
              >
                {PLATFORMS_DNS.map((p) => (
                  <option key={p.id} value={p.id} style={{ background: 'var(--l2)' }}>{p.label}</option>
                ))}
              </select>

              <ol className="space-y-1.5 mb-4 pl-5 list-decimal" style={{ color: 'rgba(240,237,248,0.78)' }}>
                {DNS_INSTRUCTIONS[platform].map((s, i) => (
                  <li key={i} className="font-body text-sm">{s}</li>
                ))}
              </ol>

              <div
                className="p-3 rounded flex items-center justify-between gap-3 mb-2"
                style={{ background: 'var(--bg)', border: '1px solid var(--b1)' }}
              >
                <code className="font-mono text-xs break-all" style={{ color: 'var(--ok)' }}>
                  {dnsValue}
                </code>
                <CopyButton value={dnsValue} testId="domain-copy-dns" />
              </div>
              <p className="font-body text-xs mb-4" style={{ color: 'var(--ts)' }}>
                DNS changes usually take a few minutes but can take up to 24 hours.
              </p>
            </div>
          )}

          {/* File tab */}
          {method === 'file' && (
            <div className="flex-1">
              <p className="font-body text-sm mb-3" style={{ color: 'rgba(240,237,248,0.78)' }}>
                Download this file and upload it to your website root folder.
              </p>

              <button
                type="button"
                data-testid="domain-download-file"
                onClick={() => triggerDownloadHtml(verifyToken)}
                className="inline-flex items-center gap-2 font-body text-sm h-10 px-4 rounded-[3px] mb-4"
                style={{ background: 'var(--a)', color: 'var(--white)' }}
              >
                <Download size={14} /> Download acodia-verify-{verifyToken.slice(0, 8)}.html
              </button>

              <label className="font-body text-xs mb-1.5 block" style={{ color: 'var(--ts)' }}>
                Which platform is this site on?
              </label>
              <select
                data-testid="domain-file-platform"
                value={cmsPlatform}
                onChange={(e) => setCmsPlatform(e.target.value)}
                className="w-full font-body text-sm h-10 px-3 rounded-[3px] mb-4"
                style={{ background: 'rgba(255,255,255,0.025)', border: '1px solid var(--b1)', color: 'var(--white)' }}
              >
                {PLATFORMS_CMS.map((p) => (
                  <option key={p.id} value={p.id} style={{ background: 'var(--l2)' }}>{p.label}</option>
                ))}
              </select>

              {FILE_INSTRUCTIONS[cmsPlatform]?.steps && (
                <ol className="space-y-1.5 mb-4 pl-5 list-decimal" style={{ color: 'rgba(240,237,248,0.78)' }}>
                  {FILE_INSTRUCTIONS[cmsPlatform].steps.map((s, i) => (
                    <li key={i} className="font-body text-sm">{s}</li>
                  ))}
                </ol>
              )}

              {FILE_INSTRUCTIONS[cmsPlatform]?.note && (
                <div
                  className="p-3 rounded mb-4"
                  style={{ background: 'rgba(251,176,64,0.08)', border: '1px solid rgba(251,176,64,0.25)' }}
                >
                  <p className="font-body text-sm" style={{ color: 'var(--warn)' }}>
                    {FILE_INSTRUCTIONS[cmsPlatform].note}
                  </p>
                  {FILE_INSTRUCTIONS[cmsPlatform].switchTo && (
                    <button
                      type="button"
                      onClick={() => setMethod(FILE_INSTRUCTIONS[cmsPlatform].switchTo)}
                      className="mt-2 inline-flex items-center gap-1 font-body text-xs px-3 py-1.5 rounded-[3px]"
                      style={{ background: 'transparent', border: '1px solid var(--b2)', color: 'var(--a-bright)' }}
                    >
                      {FILE_INSTRUCTIONS[cmsPlatform].switchLabel}
                    </button>
                  )}
                </div>
              )}

              <div
                className="p-3 rounded flex items-center justify-between gap-3 mb-4"
                style={{ background: 'var(--bg)', border: '1px solid var(--b1)' }}
              >
                <code className="font-mono text-xs break-all" style={{ color: 'var(--ok)' }}>
                  {fileUrl}
                </code>
                <a
                  href={`https://${fileUrl}`}
                  target="_blank"
                  rel="noreferrer noopener"
                  className="inline-flex items-center gap-1 font-body text-xs px-2 py-1 rounded-[3px]"
                  style={{ color: 'var(--a-bright)' }}
                >
                  <ExternalLink size={12} />
                </a>
              </div>
            </div>
          )}

          {/* Meta tab */}
          {method === 'meta' && (
            <div className="flex-1">
              <p className="font-body text-sm mb-3" style={{ color: 'rgba(240,237,248,0.78)' }}>
                Add one line of code to your homepage head section.
              </p>

              <div
                className="p-3 rounded flex items-center justify-between gap-3 mb-4"
                style={{ background: 'var(--bg)', border: '1px solid var(--b1)' }}
              >
                <code className="font-mono text-xs break-all" style={{ color: 'var(--ok)' }}>
                  {metaTag}
                </code>
                <CopyButton value={metaTag} testId="domain-copy-meta" />
              </div>

              <label className="font-body text-xs mb-1.5 block" style={{ color: 'var(--ts)' }}>
                Which platform is this site on?
              </label>
              <select
                data-testid="domain-meta-platform"
                value={cmsPlatform}
                onChange={(e) => setCmsPlatform(e.target.value)}
                className="w-full font-body text-sm h-10 px-3 rounded-[3px] mb-4"
                style={{ background: 'rgba(255,255,255,0.025)', border: '1px solid var(--b1)', color: 'var(--white)' }}
              >
                {PLATFORMS_CMS.map((p) => (
                  <option key={p.id} value={p.id} style={{ background: 'var(--l2)' }}>{p.label}</option>
                ))}
              </select>

              {META_INSTRUCTIONS[cmsPlatform]?.optionAHeading && (
                <div className="mb-3">
                  <p className="font-display text-sm font-semibold mb-2" style={{ color: 'var(--white)' }}>
                    {META_INSTRUCTIONS[cmsPlatform].optionAHeading}
                  </p>
                  <ol className="space-y-1.5 pl-5 list-decimal" style={{ color: 'rgba(240,237,248,0.78)' }}>
                    {META_INSTRUCTIONS[cmsPlatform].optionASteps.map((s, i) => (
                      <li key={i} className="font-body text-sm">{s}</li>
                    ))}
                  </ol>
                </div>
              )}
              {META_INSTRUCTIONS[cmsPlatform]?.optionBHeading && (
                <div className="mb-3">
                  <p className="font-display text-sm font-semibold mb-2" style={{ color: 'var(--white)' }}>
                    {META_INSTRUCTIONS[cmsPlatform].optionBHeading}
                  </p>
                  <ol className="space-y-1.5 pl-5 list-decimal" style={{ color: 'rgba(240,237,248,0.78)' }}>
                    {META_INSTRUCTIONS[cmsPlatform].optionBSteps.map((s, i) => (
                      <li key={i} className="font-body text-sm">{s}</li>
                    ))}
                  </ol>
                </div>
              )}
              {META_INSTRUCTIONS[cmsPlatform]?.steps && !META_INSTRUCTIONS[cmsPlatform]?.optionAHeading && (
                <ol className="space-y-1.5 mb-3 pl-5 list-decimal" style={{ color: 'rgba(240,237,248,0.78)' }}>
                  {META_INSTRUCTIONS[cmsPlatform].steps.map((s, i) => (
                    <li key={i} className="font-body text-sm">{s}</li>
                  ))}
                </ol>
              )}

              <p className="font-body text-xs mb-4" style={{ color: 'var(--ts)' }}>
                Changes may take a few minutes to appear.
              </p>
            </div>
          )}

          {localStatus.error && (
            <div
              className="p-3 rounded mb-4 flex items-start gap-2"
              style={{ background: 'rgba(248,113,113,0.08)', border: '1px solid rgba(248,113,113,0.25)' }}
              data-testid="domain-verify-error"
            >
              <AlertTriangle size={14} style={{ color: 'var(--danger)', marginTop: 2, flexShrink: 0 }} />
              <p className="font-body text-sm" style={{ color: 'var(--danger)' }}>{localStatus.error}</p>
            </div>
          )}

          <button
            type="button"
            data-testid="domain-verify-button"
            onClick={runVerify}
            disabled={verifying}
            className="w-full btn-shimmer font-body font-medium text-sm h-11 rounded-[3px] mt-auto"
            style={{ background: 'var(--a)', color: 'var(--white)', opacity: verifying ? 0.7 : 1 }}
          >
            {verifying ? (
              <span className="inline-flex items-center justify-center gap-2">
                <Loader2 size={14} className="dom-spin" /> Checking…
              </span>
            ) : 'Verify Now'}
          </button>
        </>
      )}

      <p
        className="font-body text-xs mt-4 text-center"
        style={{ color: 'var(--ts)' }}
      >
        Need help verifying this domain?{' '}
        <a
          href="mailto:connect@acodia.co.uk"
          style={{ color: 'var(--a-bright)', textDecoration: 'underline', textUnderlineOffset: 3 }}
        >
          Email connect@acodia.co.uk
        </a>{' '}
        or{' '}
        <a
          href="mailto:connect@acodia.co.uk?subject=Book%20a%20setup%20call"
          style={{ color: 'var(--a-bright)', textDecoration: 'underline', textUnderlineOffset: 3 }}
        >
          book a setup call
        </a>.
      </p>

      <style>{`
        @keyframes domSpin { to { transform: rotate(360deg); } }
        .dom-spin { animation: domSpin 1s linear infinite; }
        @keyframes domPulse { 0%, 100% { opacity: 1; } 50% { opacity: 0.35; } }
        .dom-pulse { animation: domPulse 1.6s ease-in-out infinite; }
      `}</style>
    </div>
  );
}
